class QuickView {
  constructor() {
    this.modal = null;
    this.isUpdating = false; 
    this.priceObserver = null;
    this.scrollPosition = undefined;
    this.preventTouchMove = this.preventTouchMove.bind(this);
    this.init();
  }

  init() {
    this.modal = document.getElementById('quickViewModal');
    
    if (!this.modal) {
      return;
    }
    
    document.addEventListener('click', (e) => {
      if (e.target.closest('.quick-view-button')) {
        e.preventDefault();
        e.stopPropagation();
        const button = e.target.closest('.quick-view-button');
        this.open(button);
      }
    });
    
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal && this.modal.style.display !== 'none') {
        this.close();
      }
    });
  }

  async open(button) {
    const productUrl = button.getAttribute('data-product-url');
    
    if (!productUrl) {
      return;
    }

    try {
      this.showModal();
      this.showLoading();
      
      const response = await fetch(productUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      const extractedContent = this.extractProductSection(html);
      this.renderProduct(extractedContent);
      
      await this.loadProductData(productUrl);
      
      this.hideLoading();
      
    } catch (error) {
      this.showError();
      // In caso di errore, assicurati che lo scroll sia ripristinato
      this.restoreBodyScroll();
    }
  }

  async loadProductData(productUrl) {
    try {
      const productJsonUrl = productUrl + '.js';
      const response = await fetch(productJsonUrl);
      
      if (response.ok) {
        const productData = await response.json();
        
        const container = document.getElementById('quickViewProductContent');
        if (container) {
          let productScript = container.querySelector('script[data-product-json]');
          if (!productScript) {
            productScript = document.createElement('script');
            productScript.type = 'application/json';
            productScript.setAttribute('data-product-json', '');
            container.appendChild(productScript);
          }
          productScript.textContent = JSON.stringify(productData);
        }
      }
    } catch (error) {
    }
  }

  extractProductSection(html) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    let productSection = 
      tempDiv.querySelector('[id*="blocks_section"]:has(.product-images-grid, .product-name, .variant-picker-custom, .buy-button-custom, .price-card-custom)') ||
      tempDiv.querySelector('[id*="blocks_section"]:has(.product__media, .product__info, .product-form)') ||
      tempDiv.querySelector('[data-section-type="main-product"]') ||
      tempDiv.querySelector('.main-product') ||
      tempDiv.querySelector('[id*="blocks_section"]:has([class*="grid-cols-2"])') ||
      tempDiv.querySelector('[id*="blocks_section"]:has(.variant-picker, .product-form, .buy-button)') ||
      (tempDiv.querySelectorAll('[id*="blocks_section"]').length > 1 ? tempDiv.querySelectorAll('[id*="blocks_section"]')[1] : null) ||
      tempDiv.querySelector('main') ||
      tempDiv.querySelector('.product-section');
    
    if (!productSection) {
      throw new Error('Product section not found');
    }
    
    const clonedSection = productSection.cloneNode(true);
    this.cleanupProductSection(clonedSection);
    
    return clonedSection.outerHTML;
  }

  cleanupProductSection(section) {
    const scriptsToRemove = section.querySelectorAll('script:not([type="application/json"]):not([data-product-json]):not([id*="product"]):not([class*="product"])');
    scriptsToRemove.forEach(script => script.remove());
    
    const modals = section.querySelectorAll('[id*="modal"], .modal');
    modals.forEach(modal => modal.remove());
    
    this.makeElementsUnique(section);
    
    const elementsToHide = [
      '.product-recommendations',
      '[class*="recommendation"]',
      '.reviews',
      '.related-products'
    ];
    
    elementsToHide.forEach(selector => {
      const elements = section.querySelectorAll(selector);
      elements.forEach(el => el.style.display = 'none');
    });
    
    const images = section.querySelectorAll('img[data-src]');
    images.forEach(img => {
      if (img.dataset.src) {
        img.src = img.dataset.src;
      }
    });

    this.ensureProductJSON(section);
    
    // Normalizza i nomi dei radio button per il quick view
    this.normalizeRadioButtonNames(section);
    
    // Gestisci i variant picker multipli nel quick view
    this.handleMultipleVariantPickers(section);
  }
  
  handleMultipleVariantPickers(section) {
    const variantRadiosElements = section.querySelectorAll('variant-radios');
    
    console.log('=== QUICK VIEW VARIANT PICKER DEBUG ===');
    console.log(`Found ${variantRadiosElements.length} variant-radios elements`);
    console.log(`Screen width: ${window.innerWidth}`);
    console.log(`Is mobile: ${window.innerWidth <= 768}`);
    
    if (variantRadiosElements.length > 1) {
      const isMobile = window.innerWidth <= 768;
      
      variantRadiosElements.forEach((variantRadios, index) => {
        // Cerca il container padre che potrebbe avere le classi mobile-only/desktop-only
        let container = variantRadios;
        let parentContainer = variantRadios.parentElement;
        
        // Risali nella gerarchia per trovare il container con le classi distintive
        while (parentContainer && !parentContainer.classList.contains('mobile-only') && !parentContainer.classList.contains('desktop-only')) {
          container = parentContainer;
          parentContainer = parentContainer.parentElement;
        }
        
        const containerClasses = container.className;
        const parentClasses = parentContainer ? parentContainer.className : 'none';
        
        console.log(`\nVariant picker ${index}:`);
        console.log(`  Variant picker classes: "${variantRadios.className}"`);
        console.log(`  Container classes: "${containerClasses}"`);
        console.log(`  Parent container classes: "${parentClasses}"`);
        
        // Logica di detection basata sui container padre
        const isMobileVariantPicker = 
          container.classList.contains('mobile-only') ||
          (parentContainer && parentContainer.classList.contains('mobile-only')) ||
          containerClasses.includes('mobile-only') ||
          parentClasses.includes('mobile-only');
          
        const isDesktopVariantPicker = 
          container.classList.contains('desktop-only') ||
          (parentContainer && parentContainer.classList.contains('desktop-only')) ||
          containerClasses.includes('desktop-only') ||
          parentClasses.includes('desktop-only');
        
        console.log(`  Is mobile picker: ${isMobileVariantPicker}`);
        console.log(`  Is desktop picker: ${isDesktopVariantPicker}`);
        
        // Nascondi il variant picker non appropriato
        if (isMobile && isDesktopVariantPicker) {
          console.log(`  -> Hiding desktop picker on mobile`);
          variantRadios.style.display = 'none';
        } else if (!isMobile && isMobileVariantPicker) {
          console.log(`  -> Hiding mobile picker on desktop`);
          variantRadios.style.display = 'none';
        } else {
          console.log(`  -> Keeping visible`);
          variantRadios.style.display = '';
        }
      });
    } else {
      console.log('Single variant picker - no action needed');
    }
    
    // Log finale
    setTimeout(() => {
      console.log('\n=== FINAL STATE ===');
      const visiblePickers = Array.from(variantRadiosElements).filter(vr => vr.style.display !== 'none');
      console.log(`Visible variant pickers: ${visiblePickers.length}`);
      
      if (visiblePickers.length === 1) {
        const checkedRadios = visiblePickers[0].querySelectorAll('input[type="radio"]:checked');
        console.log(`Checked radios in active picker: ${checkedRadios.length}`);
        checkedRadios.forEach((radio, rIndex) => {
          console.log(`  ${rIndex}: ${radio.name} = ${radio.value}`);
        });
      }
    }, 100);
  }
  
  normalizeRadioButtonNames(section) {
    // Trova tutti i variant-radios nel quick view
    const variantRadiosElements = section.querySelectorAll('variant-radios');
    
    variantRadiosElements.forEach((variantRadios, index) => {
      const radioButtons = variantRadios.querySelectorAll('input[type="radio"]');
      
      radioButtons.forEach(radio => {
        const currentName = radio.name;
        
        // Rimuovi suffissi come "-block123", "-standard", "-main" dal name
        const baseName = currentName.replace(/-(block\w*|standard|main)$/, '');
        
        // Per il quick view, usa un naming unico per gruppo
        // Questo evita conflitti tra variant picker mobile/desktop nel quick view
        radio.name = `${baseName}-quickview-${index}`;
        
        // Aggiorna anche il form attribute se necessario
        const quickViewForm = section.querySelector('form[data-quickview-form]');
        if (quickViewForm) {
          radio.setAttribute('form', quickViewForm.id);
        }
      });
    });
  }

  ensureProductJSON(section) {
    const existingScript = section.querySelector('script[type="application/json"][data-product-json]');
    if (existingScript) {
      return;
    }

    const productForm = section.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const productHandle = this.extractProductHandle(productForm);
      if (productHandle) {
        const script = document.createElement('script');
        script.type = 'application/json';
        script.setAttribute('data-product-json', '');
        script.setAttribute('data-product-handle', productHandle);
        section.appendChild(script);
      }
    }
  }

  makeElementsUnique(section) {
    const elementsWithId = section.querySelectorAll('[id]');
    elementsWithId.forEach(element => {
      const originalId = element.id;
      element.id = `quickview-${originalId}`;
      
      const referencingElements = section.querySelectorAll(`[for="${originalId}"], [href="#${originalId}"], [aria-describedby="${originalId}"]`);
      referencingElements.forEach(ref => {
        if (ref.getAttribute('for')) ref.setAttribute('for', `quickview-${originalId}`);
        if (ref.getAttribute('href')) ref.setAttribute('href', `#quickview-${originalId}`);
        if (ref.getAttribute('aria-describedby')) ref.setAttribute('aria-describedby', `quickview-${originalId}`);
      });
    });
    
    const forms = section.querySelectorAll('form');
    forms.forEach((form, index) => {
      if (form.name) {
        form.name = `quickview-${form.name}`;
      }
      form.setAttribute('data-quickview-form', 'true');
    });
    
    const shopifyComponents = section.querySelectorAll('variant-radios, variant-selects, product-form, quantity-input');
    shopifyComponents.forEach(component => {
      component.setAttribute('data-quickview-component', 'true');
    });
  }

  extractProductHandle(form) {
    const action = form.getAttribute('action');
    if (action && action.includes('/cart/add')) {
      const currentUrl = window.location.pathname;
      const match = currentUrl.match(/\/products\/([^\/]+)/);
      return match ? match[1] : null;
    }
    return null;
  }

  renderProduct(productHtml) {
    const contentContainer = document.getElementById('quickViewProductContent');
    if (!contentContainer) {
      return;
    }
    
    contentContainer.innerHTML = productHtml;
    
    contentContainer.setAttribute('data-quickview-container', 'true');
    
    this.initializeShopifyComponents(contentContainer);
    
    setTimeout(() => {
      this.applyQuickViewStyles(contentContainer);
    }, 200);
  }

  

  getViewFullProductText() {
    if (window.quickViewTranslations && window.quickViewTranslations.view_full_product) {
      return window.quickViewTranslations.view_full_product;
    }
    
    const language = document.documentElement.lang || navigator.language || 'it';
    
    if (language.startsWith('en')) {
      return 'View product page';
    } else {
      return 'Vedi scheda prodotto';
    }
  }

  initializeShopifyComponents(container) {
    const variantRadios = container.querySelectorAll('variant-radios[data-quickview-component]');
    variantRadios.forEach(element => {
      if (element.connectedCallback && !element.hasAttribute('data-initialized')) {
        element.setAttribute('data-initialized', 'true');
        element.connectedCallback();
        
        element.addEventListener('change', (e) => {
          e.stopPropagation();
          e.stopImmediatePropagation();
          
          setTimeout(() => {
            this.updateQuickViewContent(container);
          }, 100);
        });
      }
    });

    const variantSelects = container.querySelectorAll('variant-selects[data-quickview-component]');
    variantSelects.forEach(element => {
      if (element.connectedCallback && !element.hasAttribute('data-initialized')) {
        element.setAttribute('data-initialized', 'true');
        element.connectedCallback();
        
        element.addEventListener('change', (e) => {
          e.stopPropagation();
          e.stopImmediatePropagation();
          
          setTimeout(() => {
            this.updateQuickViewContent(container);
          }, 100);
        });
      }
    });

    const productForms = container.querySelectorAll('product-form[data-quickview-component]');
    productForms.forEach(element => {
      if (element.connectedCallback && !element.hasAttribute('data-initialized')) {
        element.setAttribute('data-initialized', 'true');
        element.connectedCallback();
      }
    });

    const quantityInputs = container.querySelectorAll('quantity-input[data-quickview-component]');
    quantityInputs.forEach(element => {
      if (element.connectedCallback && !element.hasAttribute('data-initialized')) {
        element.setAttribute('data-initialized', 'true');
        element.connectedCallback();
      }
    });

    const carousels = container.querySelectorAll('carousel-component');
    carousels.forEach(element => {
      if (element.connectedCallback && !element.hasAttribute('data-initialized')) {
        element.setAttribute('data-initialized', 'true');
        element.connectedCallback();
      }
    });

    this.initializeWishlistButtons(container);
    this.addVariantChangeListeners(container);
    this.isolateQuickViewForms(container);
    this.initializeImageSliders(container);

    if (variantRadios.length === 0 && variantSelects.length === 0) {
      this.initializeVariantSelection(container);
    } else {
      setTimeout(() => {
        this.applyQuickViewStyles(container);
      }, 100);
    }
  }

  isolateQuickViewForms(container) {
    const forms = container.querySelectorAll('form[data-quickview-form]');
    forms.forEach(form => {
      form.addEventListener('submit', (e) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        if (!form.closest('#quickViewProductContent')) {
          e.preventDefault();
          return false;
        }
      });
      
      const inputs = form.querySelectorAll('input, select, textarea, button');
      inputs.forEach(input => {
        input.addEventListener('click', (e) => {
          e.stopPropagation();
        });
        
        input.addEventListener('change', (e) => {
          e.stopPropagation();
        });
      });
    });
  }

  initializeImageSliders(container) {
    const sliders = container.querySelectorAll('.product-images-mobile-slider');
    
    sliders.forEach((slider, sliderIndex) => {
      const slidesContainer = slider.querySelector('.product-images-slides');
      const dots = slider.querySelectorAll('.product-images-dot');
      const slides = slider.querySelectorAll('.product-images-slide');
      
      if (!slidesContainer || slides.length === 0) return;
      
      if (slider.hasAttribute('data-quickview-slider-initialized')) return;
      slider.setAttribute('data-quickview-slider-initialized', 'true');
      
      let currentSlide = 0;
      const totalSlides = slides.length;
      
      function updateSlider() {
        if (slidesContainer) {
          slidesContainer.style.transform = `translateX(-${currentSlide * 100}%)`;
        }
        
        dots.forEach((dot, index) => {
          if (index === currentSlide) {
            dot.classList.add('active');
          } else {
            dot.classList.remove('active');
          }
        });
      }
      
      function nextSlide() {
        currentSlide = (currentSlide + 1) % totalSlides;
        updateSlider();
      }
      
      function prevSlide() {
        currentSlide = (currentSlide - 1 + totalSlides) % totalSlides;
        updateSlider();
      }
      
      function goToSlide(slideIndex) {
        currentSlide = slideIndex;
        updateSlider();
      }
      
      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          goToSlide(index);
        });
      });
      
      let startX = 0;
      let endX = 0;
      
      slider.addEventListener('touchstart', function(e) {
        startX = e.touches[0].clientX;
      }, { passive: true });
      
      slider.addEventListener('touchend', function(e) {
        endX = e.changedTouches[0].clientX;
        handleSwipe();
      }, { passive: true });
      
      function handleSwipe() {
        const threshold = 50;
        const diff = startX - endX;
        
        if (Math.abs(diff) > threshold) {
          if (diff > 0) {
            nextSlide();
          } else {
            prevSlide();
          }
        }
      }
      
      updateSlider();
    });
  }

  applyQuickViewStyles(container) {
    if (this.isUpdating) return; 
    this.isUpdating = true;
    
    this.addResponsiveUtilities();
    
    this.styleVariantSelection(container);
    
    this.stylePrices(container);
    
    this.initializeWishlistButtons(container);
    
    this.setupPriceStyleObserver(container);
    
    this.isUpdating = false;
  }

  addResponsiveUtilities() {
    if (document.getElementById('quick-view-responsive-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'quick-view-responsive-styles';
    style.textContent = `
      /* Quick view responsive utilities */
      #quickViewModal .mobile-only {
        display: block !important;
      }

      #quickViewModal .desktop-only {
        display: none !important;
      }

      @media (min-width: 768px) {
        #quickViewModal .mobile-only {
          display: none !important;
        }
        
        #quickViewModal .desktop-only {
          display: block !important;
        }
      }

      /* Additional responsive utilities */
      @media (max-width: 767px) {
        #quickViewModal .hide-mobile {
          display: none !important;
        }
      }

      @media (min-width: 768px) {
        #quickViewModal .hide-desktop {
          display: none !important;
        }
      }

      /* Tablet-specific utilities */
      @media (min-width: 640px) and (max-width: 1023px) {
        #quickViewModal .tablet-only {
          display: block !important;
        }
        
        #quickViewModal .hide-tablet {
          display: none !important;
        }
      }

      @media (max-width: 639px), (min-width: 1024px) {
        #quickViewModal .tablet-only {
          display: none !important;
        }
      }
    `;
    
    document.head.appendChild(style);
  }
  
  styleVariantSelection(container) {
    const radioInputs = container.querySelectorAll('input[type="radio"]');
    
    radioInputs.forEach((input, index) => {
      const label = input.nextElementSibling;
      if (label && label.tagName === 'LABEL') {
        if (input.checked) {
          label.style.setProperty('background-color', '#ffffff', 'important');
          label.style.setProperty('color', '#212121', 'important');
          label.style.setProperty('border', '1px solid #212121', 'important');
        } else {
          label.style.setProperty('background-color', '#F0EFEB', 'important');
          label.style.setProperty('color', '#212121', 'important');
          label.style.setProperty('border', '1px solid #f8f8f8', 'important');
        }
      }
    });
  }
  
  stylePrices(container) {
    const allPriceElements = container.querySelectorAll('.price, .price-card-custom, [class*="price"]');
    
    allPriceElements.forEach((priceContainer, containerIndex) => {
      const spans = priceContainer.querySelectorAll('span');
      
      spans.forEach((span, index) => {
        if (span.textContent && span.textContent.includes('€') && span.textContent.match(/\d/)) {
          const isComparePrice = span.closest('.price-item--regular, .price__compare, .compare-price') ||
                                span.classList.contains('price-item--regular') ||
                                span.classList.contains('price__compare') ||
                                span.classList.contains('compare-price') ||
                                (spans.length > 1 && index === 1);
          
          if (isComparePrice) {
            span.style.setProperty('color', '#696C6B', 'important');
            span.style.setProperty('text-decoration', 'line-through', 'important');
            span.style.setProperty('font-weight', 'normal', 'important');
            span.style.setProperty('font-size', '14px', 'important');
            span.style.setProperty('transition', 'none', 'important');
          } else {
            span.style.setProperty('color', '#B90B2E', 'important');
            span.style.setProperty('font-weight', 'bold', 'important');
            span.style.setProperty('font-size', '20px', 'important');
            span.style.setProperty('text-decoration', 'none', 'important');
            span.style.setProperty('transition', 'none', 'important');
          }
        }
      });
    });
  }

  handleVariantChange(container) {
    this.stylePrices(container);
    this.updateVariantPricing(container);
    
    setTimeout(() => {
      this.stylePrices(container);
    }, 10);
    
    setTimeout(() => {
      this.stylePrices(container);
    }, 100);
  }

  updateVariantPricing(container) {
    const selectedOptions = [];
    const visibleVariantRadios = Array.from(container.querySelectorAll('variant-radios'))
      .filter(vr => vr.style.display !== 'none');
    
    const activeVariantRadios = visibleVariantRadios.length > 0 ? visibleVariantRadios[0] : null;
    
    if (activeVariantRadios) {
      const variantInputs = activeVariantRadios.querySelectorAll('input[type="radio"]:checked');
      variantInputs.forEach(input => {
        selectedOptions.push(input.value);
      });
    } else {
      const variantInputs = container.querySelectorAll('input[type="radio"]:checked');
      variantInputs.forEach(input => {
        selectedOptions.push(input.value);
      });
    }

    const productScript = container.querySelector('script[type="application/json"][data-product-json]');
    if (!productScript) {
      return;
    }

    let productData;
    try {
      productData = JSON.parse(productScript.textContent);
    } catch (e) {
      return;
    }

    const selectedVariant = productData.variants.find(variant => {
      return variant.options.every((option, index) => {
        return option === selectedOptions[index];
      });
    });

    if (selectedVariant) {
      this.updatePriceDisplay(container, selectedVariant);
      this.updateFormInputs(container, selectedVariant);
      this.updateAddToCartButton(container, selectedVariant);
    }
  }

  updatePriceDisplay(container, variant) {
    const priceElements = container.querySelectorAll('.price, .price-card-custom, [class*="price"]');
    
    priceElements.forEach(priceElement => {
      const priceSpans = priceElement.querySelectorAll('span');
      
      if (priceSpans.length > 0) {
        const currentPriceSpan = priceSpans[0];
        const formattedPrice = this.formatMoney(variant.price);
        currentPriceSpan.textContent = formattedPrice;
        
        if (variant.compare_at_price && variant.compare_at_price > variant.price && priceSpans.length > 1) {
          const comparePriceSpan = priceSpans[1];
          const formattedComparePrice = this.formatMoney(variant.compare_at_price);
          comparePriceSpan.textContent = formattedComparePrice;
          comparePriceSpan.style.display = '';
          
          if (!priceElement.classList.contains('price--on-sale')) {
            priceElement.classList.add('price--on-sale');
          }
        } else if (priceSpans.length > 1) {
          priceSpans[1].style.display = 'none';
          priceElement.classList.remove('price--on-sale');
        }
      }
    });
    
    setTimeout(() => {
      this.stylePrices(container);
    }, 10);
  }

  setupPriceStyleObserver(container) {
    if (this.priceObserver) {
      this.priceObserver.disconnect();
    }

    this.priceObserver = new MutationObserver((mutations) => {
      let shouldReapplyStyles = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          const target = mutation.target;
          
          if (target.closest && target.closest('.price, .price-card-custom, [class*="price"]')) {
            shouldReapplyStyles = true;
          }
          
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.matches && node.matches('.price *, .price-card-custom *, [class*="price"] *')) {
                shouldReapplyStyles = true;
              }
            }
          });
        }
      });

      if (shouldReapplyStyles && !this.isUpdating) {
        this.stylePrices(container);
      }
    });

    const priceContainers = container.querySelectorAll('.price, .price-card-custom, [class*="price"]');
    priceContainers.forEach((priceContainer) => {
      this.priceObserver.observe(priceContainer, {
        childList: true,
        subtree: true,
        characterData: true
      });
    });
  }

  updateQuickViewContent(container) {
    this.handleVariantChange(container);
    this.initializeImageSliders(container);
  }

  addVariantChangeListeners(container) {
    const radioInputs = container.querySelectorAll('input[type="radio"]');
    console.log(`=== ADDING LISTENERS TO ${radioInputs.length} RADIO BUTTONS ===`);
    
    radioInputs.forEach((input, index) => {
      console.log(`Radio ${index}: ${input.name} = ${input.value} (checked: ${input.checked})`);
      
      input.addEventListener('click', (e) => {
        console.log(`\n=== RADIO CLICKED: ${input.value} ===`);
        console.log(`Radio name: ${input.name}`);
        console.log(`Is in visible variant picker: ${input.closest('variant-radios').style.display !== 'none'}`);
        
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        setTimeout(() => {
          this.styleVariantSelection(container);
          this.stylePrices(container);
        }, 0);
      });
      
      input.addEventListener('change', (e) => {
        console.log(`\n=== RADIO CHANGED: ${input.value} ===`);
        console.log(`Radio name: ${input.name}`);
        console.log(`Is in visible variant picker: ${input.closest('variant-radios').style.display !== 'none'}`);
        
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        this.styleVariantSelection(container);
        this.handleVariantChange(container);
      });
    });
    
    const variantSections = container.querySelectorAll('variant-radios, variant-selects');
    variantSections.forEach(section => {
      section.addEventListener('change', (e) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        this.styleVariantSelection(container);
        this.handleVariantChange(container);
      });
    });
    
    container.addEventListener('click', (e) => {
      e.stopPropagation();
    });
    
    container.addEventListener('change', (e) => {
      e.stopPropagation();
    });
  }

  initializeWishlistButtons(container) {
    const wishlistButtons = container.querySelectorAll('[button-wishlist]');
    
    if (wishlistButtons.length === 0) return;
    
    wishlistButtons.forEach((button) => {
      const productHandle = button.dataset.productHandle || false;
      if (!productHandle) {
        console.error('[Shopify Wishlist] Missing `data-product-handle` attribute. Failed to update the wishlist.');
        return;
      }
      
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);
      
      if (this.wishlistContains(productHandle)) {
        newButton.classList.add('active');
      }
      
      newButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.updateWishlist(productHandle);
        newButton.classList.toggle('active');
      });
    });
  }

  getWishlist() {
    const LOCAL_STORAGE_WISHLIST_KEY = 'shopify-wishlist';
    const LOCAL_STORAGE_DELIMITER = ',';
    const wishlist = localStorage.getItem(LOCAL_STORAGE_WISHLIST_KEY) || false;
    if (wishlist) return wishlist.split(LOCAL_STORAGE_DELIMITER);
    return [];
  }

  setWishlist(array) {
    const LOCAL_STORAGE_WISHLIST_KEY = 'shopify-wishlist';
    const LOCAL_STORAGE_DELIMITER = ',';
    const wishlist = array.join(LOCAL_STORAGE_DELIMITER);
    if (array.length) localStorage.setItem(LOCAL_STORAGE_WISHLIST_KEY, wishlist);
    else localStorage.removeItem(LOCAL_STORAGE_WISHLIST_KEY);

    const counters = document.querySelectorAll('.wishlist-counter');
    counters.forEach((counter) => {
      counter.innerHTML = '';
      if (array.length > 0) counter.innerHTML = array.length;
    });

    const event = new CustomEvent('shopify-wishlist:updated', {
      detail: { wishlist: array }
    });
    document.dispatchEvent(event);

    return wishlist;
  }

  updateWishlist(handle) {
    const wishlist = this.getWishlist();
    const indexInWishlist = wishlist.indexOf(handle);
    
    if (indexInWishlist === -1) wishlist.push(handle);
    else wishlist.splice(indexInWishlist, 1);

    return this.setWishlist(wishlist);
  }

  wishlistContains(handle) {
    const wishlist = this.getWishlist();
    return wishlist.includes(handle);
  }

  initializeVariantSelection(container) {
    const variantInputs = container.querySelectorAll('input[type="radio"]');
    const priceElement = container.querySelector('.price, .price-card-custom, [class*="price"]');
    const formInputs = container.querySelectorAll('input[name="id"]');

    if (variantInputs.length === 0) return;

    variantInputs.forEach(input => {
      input.addEventListener('change', () => {
        this.updateVariant(container);
      });
    });

    this.updateVariant(container);
  }

  updateVariant(container) {
    const selectedOptions = [];
    const visibleVariantRadios = Array.from(container.querySelectorAll('variant-radios'))
      .filter(vr => vr.style.display !== 'none');
    
    const activeVariantRadios = visibleVariantRadios.length > 0 ? visibleVariantRadios[0] : null;
    
    if (activeVariantRadios) {
      const variantInputs = activeVariantRadios.querySelectorAll('input[type="radio"]:checked');
      variantInputs.forEach(input => {
        selectedOptions.push(input.value);
      });
    } else {
      const variantInputs = container.querySelectorAll('input[type="radio"]:checked');
      variantInputs.forEach(input => {
        selectedOptions.push(input.value);
      });
    }

    const productScript = container.querySelector('script[type="application/json"][data-product-json]');
    if (!productScript) {
      return;
    }

    let productData;
    try {
      productData = JSON.parse(productScript.textContent);
    } catch (e) {
      return;
    }

    const selectedVariant = productData.variants.find(variant => {
      return variant.options.every((option, index) => {
        return option === selectedOptions[index];
      });
    });

    if (selectedVariant) {
      this.updatePrice(container, selectedVariant);
      
      this.updateFormInputs(container, selectedVariant);
      
      this.updateAddToCartButton(container, selectedVariant);
    }
  }

  updatePrice(container, variant) {
    const priceElements = container.querySelectorAll('.price, .price-card-custom, [class*="price"]');
    
    priceElements.forEach(priceElement => {
      const currentPriceEl = priceElement.querySelector('.price-item--sale, .price__current, .current-price, [class*="current"]') || 
                            priceElement.querySelector('.money') || 
                            priceElement.querySelector('span');
      
      const comparePriceEl = priceElement.querySelector('.price-item--regular, .price__compare, .compare-price, [class*="compare"]');
      const discountBadge = priceElement.querySelector('.discount-badge');

      if (currentPriceEl) {
        const formattedPrice = this.formatMoney(variant.price);
        
        const moneyElement = currentPriceEl.querySelector('.money');
        if (moneyElement) {
          moneyElement.textContent = formattedPrice;
        } else {
          currentPriceEl.textContent = formattedPrice;
        }
        
      }

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const formattedComparePrice = this.formatMoney(variant.compare_at_price);
        
        if (comparePriceEl) {
          const compareMoneyEl = comparePriceEl.querySelector('.money');
          if (compareMoneyEl) {
            compareMoneyEl.textContent = formattedComparePrice;
          } else {
            comparePriceEl.textContent = formattedComparePrice;
          }
          
          comparePriceEl.style.display = '';
        }
        
        if (discountBadge && variant.compare_at_price > 0) {
          const discountAmount = variant.compare_at_price - variant.price;
          const discountPercentage = Math.round((discountAmount / variant.compare_at_price) * 100);
          discountBadge.textContent = `-${discountPercentage}%`;
        }
        
        if (!priceElement.classList.contains('price--on-sale')) {
          priceElement.classList.add('price--on-sale');
        }
      } else {
        if (comparePriceEl) {
          comparePriceEl.style.display = 'none';
        }
        
        if (discountBadge) {
          discountBadge.style.display = 'none';
        }
        
        priceElement.classList.remove('price--on-sale');
      }
      
    });
    
    setTimeout(() => {
      this.applyQuickViewStyles(container);
    }, 10);
  }

  updateFormInputs(container, variant) {
    const formInputs = container.querySelectorAll('input[name="id"]');
    formInputs.forEach(input => {
      input.value = variant.id;
    });
    
    const variantIdInputs = container.querySelectorAll('input[name="variant_id"], input[name="product-variant-id"]');
    variantIdInputs.forEach(input => {
      input.value = variant.id;
    });
  }

  updateAddToCartButton(container, variant) {
    const addToCartButtons = container.querySelectorAll('.add-to-cart-button, button[name="add"], .buy-button');
    
    addToCartButtons.forEach(button => {
      if (variant.available) {
        button.disabled = false;
        const addText = window.quickViewTranslations?.add_to_cart || 'Aggiungi al carrello';
        const textSpan = button.querySelector('span');
        if (textSpan) {
          textSpan.textContent = addText;
        } else {
          button.textContent = addText;
        }
      } else {
        button.disabled = true;
        const soldOutText = window.quickViewTranslations?.sold_out || 'Esaurito';
        const textSpan = button.querySelector('span');
        if (textSpan) {
          textSpan.textContent = soldOutText;
        } else {
          button.textContent = soldOutText;
        }
      }
    });
  }

  formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    
    const euros = (cents / 100).toFixed(2);
    return `€${euros.replace('.', ',')}`;
  }

  showModal() {
    if (!this.modal) return;
    
    this.preventBodyScroll();
    
    this.modal.style.display = 'flex';
    this.modal.offsetHeight;
    
    requestAnimationFrame(() => {
      this.modal.style.opacity = '1';
    });
  }

  preventBodyScroll() {
    this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.width = '100%';
    
    document.documentElement.style.overflow = 'hidden';
    
    this.modal.addEventListener('touchmove', this.preventTouchMove, { passive: false });
  }

  preventTouchMove(e) {
    const target = e.target;
    const modalContent = document.querySelector('#quickViewProductContent');
    
    if (modalContent && modalContent.contains(target)) {
      return;
    }
    
    e.preventDefault();
  }

  restoreBodyScroll() {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.documentElement.style.overflow = '';
    
    if (this.scrollPosition !== undefined) {
      window.scrollTo(0, this.scrollPosition);
      this.scrollPosition = undefined;
    }
    
    if (this.modal) {
      this.modal.removeEventListener('touchmove', this.preventTouchMove);
    }
  }

  showLoading() {
    if (!this.modal) return;
    this.modal.classList.remove('loaded');
    const loadingElement = this.modal.querySelector('.quick-view-loading');
    if (loadingElement) {
      loadingElement.style.display = 'flex';
    }
  }

  hideLoading() {
    if (!this.modal) return;
    this.modal.classList.add('loaded');
    
    const loadingElement = this.modal.querySelector('.quick-view-loading');
    if (loadingElement) {
      loadingElement.style.display = 'none';
    }
  }

  showError() {
    const contentContainer = document.getElementById('quickViewProductContent');
    if (!contentContainer) return;
    
    contentContainer.innerHTML = `
      <div class="quick-view-error">
        <div class="error-content">
          <h3>${window.quickViewTranslations?.error_heading || 'Loading Error'}</h3>
          <p>${window.quickViewTranslations?.error_message || 'Unable to load product details.'}</p>
          <button onclick="quickViewInstance.close()" class="button button--primary">${window.quickViewTranslations?.close_button || 'Close'}</button>
        </div>
      </div>
    `;
    this.hideLoading();
  }

  close() {
    if (!this.modal) return;
    
    if (this.priceObserver) {
      this.priceObserver.disconnect();
      this.priceObserver = null;
    }
    
    const responsiveStyles = document.getElementById('quick-view-responsive-styles');
    if (responsiveStyles) {
      responsiveStyles.remove();
    }
    
    this.modal.style.opacity = '0';
    setTimeout(() => {
      this.modal.style.display = 'none';
      
      this.restoreBodyScroll();
      
      const contentContainer = document.getElementById('quickViewProductContent');
      if (contentContainer) {
        contentContainer.innerHTML = '';
      }
      
      this.modal.classList.remove('loaded');
    }, 300);
  }
}

function openQuickView(button, event) {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  
  if (window.quickViewInstance) {
    window.quickViewInstance.open(button);
  }
}

function closeQuickView() {
  if (window.quickViewInstance) {
    window.quickViewInstance.close();
  }
}

document.addEventListener('DOMContentLoaded', function() {
  window.quickViewInstance = new QuickView();
});
